import classNames from 'classnames'
import React, {
  type ReactElement,
  useContext,
  useEffect,
  useState
} from 'react'
import { Container, Nav, Navbar } from 'react-bootstrap'

import { ColorContext } from '@/contexts/color.context'
import { anchorFormatted } from '@/utils/format'
import {
  getDatoCmsPctPageNavigationLabel,
  NavigationItem
} from '@/utils/get-navigation-label'

import * as styles from './menu-page.module.scss'
export type MenuPageProps = {
  blocks: NavigationItem[]
}

const MenuPage = (props: Readonly<MenuPageProps>): ReactElement | null => {
  const { blocks } = props
  const colour = useContext(ColorContext)
  const [openMenuInner, setOpenMenuInner] = useState(false)
  const [menuPageLabel, setMenuPageLabel] = useState('Sections')

  // Only fill navigation with blocks that have a navigationLabel
  const navigation: NavigationItem[] = (blocks ?? [])
    .map((block) => {
      const navigationLabel: string = getDatoCmsPctPageNavigationLabel(block)
      if (navigationLabel === '') {
        return null
      }
      const item: NavigationItem = {
        id: block?.id ?? '',
        navigationLabel
      }
      return item
    })
    .filter((item): item is NavigationItem => item !== null)

  // Sync menu label with current hash (initial load + navigation)
  useEffect(() => {
    // Early return if no navigation items
    if (navigation.length === 0) {
      return
    }

    const updateMenuLabel = () => {
      const hash = window.location.hash.slice(1)
      if (hash) {
        const navItem = navigation.find(
          (item) =>
            item.navigationLabel &&
            anchorFormatted(item.navigationLabel) === hash
        )
        if (navItem?.navigationLabel) {
          setMenuPageLabel(navItem.navigationLabel)
        }
      } else {
        setMenuPageLabel('Sections')
      }
    }

    // Update on initial load
    updateMenuLabel()

    // Update on hash changes (includes browser back/forward)
    window.addEventListener('hashchange', updateMenuLabel)

    return () => {
      window.removeEventListener('hashchange', updateMenuLabel)
    }
  }, [navigation])

  if (navigation.length === 0) {
    return null
  }

  const handleSmoothScroll = (eventKey: string, navigationLabel: string) => {
    // Update the menu label
    setMenuPageLabel(navigationLabel)

    // Simple solution: always force a fresh navigation by clearing and setting hash
    const currentHash = window.location.hash.slice(1)

    if (currentHash === eventKey) {
      // If clicking the same section, temporarily clear hash then set it again
      // This forces consistent behavior without breaking navigation
      window.history.replaceState(
        null,
        '',
        window.location.pathname + window.location.search
      )

      // Small delay to ensure the clear takes effect, then set the hash
      // gatsby-plugin-anchor-links will handle the actual scrolling
      setTimeout(() => {
        window.location.hash = eventKey
      }, 50)
    } else {
      // Different section - normal hash update
      window.location.hash = eventKey
    }
  }

  const handlerMenuInner = (): void => {
    setOpenMenuInner((prev) => !prev)
  }
  return (
    <Navbar
      sticky="top"
      bg="white"
      className={classNames(styles.navbar, 'menu-page')}
      style={{ borderColor: colour?.colour?.hex ? colour?.colour?.hex : '' }}
    >
      <Container>
        <div
          className={`d-block d-lg-none pt-2 pb-2 ${styles.dropDownMenu} ${
            openMenuInner ? styles.open : ''
          }`}
          onClick={handlerMenuInner}
        >
          {menuPageLabel}
        </div>
        <Nav className="w-100 justify-content-center d-none d-lg-flex" as="ul">
          {navigation?.map(({ id, navigationLabel }, index) => {
            const eventKey: string = anchorFormatted(navigationLabel ?? '')
            const uniqueKey = id || `nav-${index}`
            return (
              <Nav.Item
                key={uniqueKey}
                as="li"
                className="pt-md-2 pb-md-2"
                onClick={handlerMenuInner}
              >
                <Nav.Link
                  eventKey={eventKey}
                  href={`#${eventKey}`}
                  onClick={(e) => {
                    e.preventDefault() // Prevent default anchor behavior
                    handleSmoothScroll(eventKey, navigationLabel ?? '')
                  }}
                >
                  {navigationLabel}
                </Nav.Link>
              </Nav.Item>
            )
          })}
        </Nav>
      </Container>
    </Navbar>
  )
}
export default MenuPage
